import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { DateFilterValue, StatusFilter } from './constants';

export interface TransactionListFiltersState {
  status: StatusFilter;
  onlyMine: boolean;
  employeeId: string | null;
  search: string;
  currencies: string[];
  types: string[];
  dateIn: DateFilterValue;
  dateOut: DateFilterValue;
  archived: boolean;
  page: number;
}

export interface TransactionListFiltersApi extends TransactionListFiltersState {
  setStatus: (v: StatusFilter) => void;
  setOnlyMine: (v: boolean) => void;
  setEmployeeId: (v: string | null) => void;
  setSearch: (v: string) => void;
  setCurrencies: (v: string[]) => void;
  setTypes: (v: string[]) => void;
  setDateIn: (v: DateFilterValue) => void;
  setDateOut: (v: DateFilterValue) => void;
  setArchived: (v: boolean) => void;
  setPage: (v: number) => void;
}

function parseStatus(raw: string | null): StatusFilter {
  return raw === 'open' || raw === 'closed' ? raw : 'all';
}

function parseCsv(raw: string | null): string[] {
  if (!raw) return [];
  return raw.split(',').map(s => s.trim()).filter(Boolean);
}

function parseDateFilter(
  preset: string | null,
  from: string | null,
  to: string | null,
): DateFilterValue {
  if (from && to) return { type: 'range', from, to };
  if (preset) return { type: 'preset', key: preset };
  return null;
}

function writeDateFilter(
  params: URLSearchParams,
  baseKey: string,
  value: DateFilterValue,
) {
  params.delete(baseKey);
  params.delete(`${baseKey}From`);
  params.delete(`${baseKey}To`);
  if (!value) return;
  if (value.type === 'preset') {
    params.set(baseKey, value.key);
    return;
  }
  if (value.from && value.to) {
    params.set(`${baseKey}From`, value.from);
    params.set(`${baseKey}To`, value.to);
  }
}

/**
 * Derives transaction list filter state from URL (useSearchParams), exposes setters that
 * write back to URL. All setters except `setPage` reset `page` to 1. Default values are
 * omitted from the URL to keep shareable links clean:
 *   - status=all, mine=1, page=1, archived=false, empty multi-selects → no query param.
 */
export function useTransactionListFilters(): TransactionListFiltersApi {
  const [searchParams, setSearchParams] = useSearchParams();

  const state: TransactionListFiltersState = useMemo(
    () => ({
      status: parseStatus(searchParams.get('status')),
      onlyMine: searchParams.get('mine') !== '0',
      employeeId: searchParams.get('emp') || null,
      search: searchParams.get('q') ?? '',
      currencies: parseCsv(searchParams.get('cur')),
      types: parseCsv(searchParams.get('types')),
      dateIn: parseDateFilter(
        searchParams.get('din'),
        searchParams.get('dinFrom'),
        searchParams.get('dinTo'),
      ),
      dateOut: parseDateFilter(
        searchParams.get('dout'),
        searchParams.get('doutFrom'),
        searchParams.get('doutTo'),
      ),
      archived: searchParams.get('archived') === '1',
      page: Math.max(1, parseInt(searchParams.get('page') ?? '1', 10) || 1),
    }),
    [searchParams],
  );

  const patch = useCallback(
    (mutate: (p: URLSearchParams) => void, { resetPage = true }: { resetPage?: boolean } = {}) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        mutate(next);
        if (resetPage) next.delete('page');
        return next;
      });
    },
    [setSearchParams],
  );

  const setStatus = useCallback(
    (v: StatusFilter) =>
      patch(p => {
        if (v === 'all') p.delete('status');
        else p.set('status', v);
      }),
    [patch],
  );

  const setOnlyMine = useCallback(
    (v: boolean) =>
      patch(p => {
        if (v) p.delete('mine');
        else p.set('mine', '0');
      }),
    [patch],
  );

  const setEmployeeId = useCallback(
    (v: string | null) =>
      patch(p => {
        if (!v) p.delete('emp');
        else p.set('emp', v);
      }),
    [patch],
  );

  const setSearch = useCallback(
    (v: string) =>
      patch(p => {
        if (!v) p.delete('q');
        else p.set('q', v);
      }),
    [patch],
  );

  const setCurrencies = useCallback(
    (v: string[]) =>
      patch(p => {
        if (v.length === 0) p.delete('cur');
        else p.set('cur', v.join(','));
      }),
    [patch],
  );

  const setTypes = useCallback(
    (v: string[]) =>
      patch(p => {
        if (v.length === 0) p.delete('types');
        else p.set('types', v.join(','));
      }),
    [patch],
  );

  const setDateIn = useCallback(
    (v: DateFilterValue) => patch(p => writeDateFilter(p, 'din', v)),
    [patch],
  );

  const setDateOut = useCallback(
    (v: DateFilterValue) => patch(p => writeDateFilter(p, 'dout', v)),
    [patch],
  );

  const setArchived = useCallback(
    (v: boolean) =>
      patch(p => {
        if (v) p.set('archived', '1');
        else p.delete('archived');
      }),
    [patch],
  );

  const setPage = useCallback(
    (n: number) =>
      patch(
        p => {
          if (n <= 1) p.delete('page');
          else p.set('page', String(n));
        },
        { resetPage: false },
      ),
    [patch],
  );

  return {
    ...state,
    setStatus,
    setOnlyMine,
    setEmployeeId,
    setSearch,
    setCurrencies,
    setTypes,
    setDateIn,
    setDateOut,
    setArchived,
    setPage,
  };
}
